import { Component, computed, effect, inject, input, untracked } from '@angular/core'

import { SystemType } from '@flowx/designer/modules/integration-designer/models/system.entity'
import { FlxResourcesGridComponent } from '@flowx/designer/modules/projects/components/resources-grid/resources-grid.component'
import { ApplicationDetailsStore } from '@flowx/designer/modules/projects/store/application-version-details.store'
import { FlxDateCellComponent } from '@flowx/designer/shared/components/ag-grid/cell-components/date-cell.component'
import {
  ApplicationResourceType,
  filterGroupedResources,
} from '@flowx/designer/shared/entities/application.entity'
import {
  ProcessData,
  ProcessInstanceInitiator,
} from '@flowx/designer/shared/entities/process-instance.types'

@Component({
  selector: 'flx-instance-trigger',
  templateUrl: './instance-trigger.component.html',
  imports: [FlxResourcesGridComponent],
})
export class InstanceTriggerComponent {
  #applicationDetailsStore = inject(ApplicationDetailsStore)

  process = input<ProcessData>(null)

  rowData = computed(() => {
    const process = this.process()
    if (!process?.initiator) {
      return []
    }

    return [this.#buildTriggerRow(process)]
  })

  readonly triggerTableColumns = [
    { headerName: 'Trigger Type', field: 'triggerType' },
    { headerName: 'Trigger Name', field: 'triggerName' },
    { headerName: 'Process Name', field: 'processName' },
    {
      headerName: 'Timestamp',
      field: 'timestamp',
      cellRenderer: FlxDateCellComponent,
      flex: 0,
      width: 220,
    },
  ]

  constructor() {
    effect(() => {
      const process = this.process()
      if (process?.initiator) {
        untracked(() =>
          this.#applicationDetailsStore.getResourcesByType(ApplicationResourceType.System, {
            types: [SystemType.EMAIL_TRIGGER, SystemType.MS_GRAPH],
            scopes: ['READ'],
          })
        )
      }
    })
  }

  #buildTriggerRow(process: ProcessData): {
    triggerType: string
    triggerName: string
    processName: string
    timestamp: string | null
  } {
    return {
      triggerType: this.#resolveTriggerType(process.initiator),
      triggerName: this.#resolveTriggerName(process.initiator),
      processName: process.definition?.resourceName ?? '-',
      timestamp: process.initiator?.inputValues?.emailMessage?.dateTime ?? null,
    }
  }

  #resolveTriggerName(initiator: ProcessInstanceInitiator): string {
    const systems = this.#applicationDetailsStore.systems()
    if (!systems?.loaded || !systems?.values) {
      return '-'
    }

    const appName = this.#applicationDetailsStore.appName()
    const groupedTriggers = filterGroupedResources(systems.values, null, appName)
    const allTriggers = groupedTriggers.flatMap((group) => group.items)
    const matched = allTriggers.find(
      (trigger) => trigger.resourceDefinitionId === initiator.triggerResourceDefinitionId
    )
    return matched?.name ?? '-'
  }

  #resolveTriggerType(initiator: ProcessInstanceInitiator): string {
    return initiator?.initiatorType ?? '-'
  }
}
